import React, { FunctionComponent, useState, useCallback, useEffect, useMemo, ChangeEvent, FormEvent } from 'react';
import cx from 'classnames';
import loginStyles from './styles/login.scss';
import logo from '../../assets/images/rtspro-logo-blue.svg';
import close from '../../assets/images/closeIcon.svg';
import { Snackbar, Spinner } from 'shamrock-clover-ui';
import LocalAuth from '@shamrock-core/common/authentication/local-authentication';
import { useSearchParams } from 'react-router-dom';
import ForgotPassword from './ForgotPassword';
import { V2LoginService } from '../../services/login.v2.service';
import { Variant } from 'shamrock-clover-ui/dist/clover/components/Snackbar/Snackbar';
import { isInternal, redirectToAdfs, useFinalDestination, validatedFinalDestination } from './utils';
import DeviceLimitModal from './deviceLimits/DeviceLimitModal';
import sessionService from '../../services/session.service';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { setDeviceId, setLoginDetails } from '../../redux/actions/appActions';
import { RootState } from 'bootstrap';
import { RequestVerification } from './RequestVerification';
import { MFALogin } from './MFALogin';
import { PasswordResetScreens } from '../passwordReset/passwordResetScreens';
import { v4 as uuidv4 } from 'uuid';

interface LoginProps {
  closeLogin?: () => void;
  sendAnalyticsEvent?: (arg: string) => void;
  setRerender?: React.Dispatch<React.SetStateAction<number>>;
}

export enum LoginForms {
  VERIFICATION_REQUEST = 'VERIFICATION_REQUEST',
  MFA_LOGIN = 'MFA_LOGIN',
  FORGOT_PASSWORD = 'FORGOT_PASSWORD',
  FORCE_PASSWORD_RESET = 'FORCE_PASSWORD_RESET',
  PASSWORD_RESET_FORM = 'PASSWORD_RESET_FORM',
  PASSWORD_RESET_SENT = 'PASSWORD_RESET_SENT',
}

// Provides us better inferences when narrowing scope via key values
export type MFAError =
  | {
    key: '';
    metadata: Record<string, never>;
  }
  | {
    key: 'bad_value';
    metadata: { remaining: number };
  }
  | {
    key: 'timeout';
    metadata: { timeout: number };
  }
  | {
    key: 'account_locked';
    metadata: Record<string, never>;
  }
  | {
    key: 'throttled';
    metadata: Record<string, never>;
  };

const loginResetForms = [
  LoginForms.FORCE_PASSWORD_RESET,
  LoginForms.PASSWORD_RESET_FORM,
  LoginForms.PASSWORD_RESET_SENT,
];

const Login: FunctionComponent<LoginProps> = ({ closeLogin, sendAnalyticsEvent, setRerender }) => {
  const features = useSelector<RootState, string[]>((state) => state.app.features);
  const cpua4769Web = features.includes('CPUA-4769-WEB');
  const cpua5206 = features.includes('CPUA-5206');

  const dispatch = useDispatch();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [cloneToken, setCloneToken] = useState(null);
  const [showDeviceLimitModal, setShowDeviceLimitModal] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const [snackbarDuration, setSnackbarDuration] = useState(0);
  const [snackbarVariant, setSnackbarVariant] = useState('' as Variant);
  const [currentForm, setCurrentForm] = useState<LoginForms>(
    LoginForms.VERIFICATION_REQUEST,
  );
  const [isLoading, setIsLoading] = useState(false);
  const loginService = useMemo(() => (new V2LoginService()), []);
  const [searchParams] = useSearchParams();
  const [mfaError, setMfaError] = useState<MFAError>({ key: '', metadata: {} });

  const redirectFinalDestination = useFinalDestination(setRerender);
  const navigateForgotPassword = (e: FormEvent) => {
    e.preventDefault();
    navigate(LoginForms.FORGOT_PASSWORD, true);
  };
  const handleClearState = () => {
    setEmail('');
    setPassword('');
    setVerificationCode('');
    setMfaError({ key: '', metadata: {} });
    setShowSnackbar(false);
  };
  const navigate = (route: LoginForms, clearState?: boolean) => {
    if (clearState) {
      handleClearState();
    }
    setCurrentForm(route);
  };
  const closeForgotPassword = () => {
    setEmail('');
    return setCurrentForm(LoginForms.VERIFICATION_REQUEST);
  };
  const { t } = useTranslation();
  const closeDeviceLimitModal = () => {
    if (currentForm === LoginForms.MFA_LOGIN) {
      navigate(LoginForms.VERIFICATION_REQUEST, true);
    } else {
      setEmail('');
      setPassword('');
    }
    setShowDeviceLimitModal(false);
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value.trim();
    setEmail(inputValue);
  };

  const handleLogin = useCallback(async () => {
    setMfaError({ key: '', metadata: {} });
    setShowSnackbar(false);
    setIsLoading(true);
    try {
      const loweredEmail = email.toLowerCase();
      dispatch(setLoginDetails({ email: loweredEmail }));

      if (cpua5206) {
        const deviceId = uuidv4();
        localStorage.setItem('device_id', deviceId);
        dispatch(setDeviceId(deviceId));
      }

      const loginResults = await loginService.login(loweredEmail, password, verificationCode);
      LocalAuth.setToken({ ...loginResults, email: loginResults.email.toLowerCase() });
      redirectFinalDestination({ ...loginResults, email: loginResults.email.toLowerCase() });
    } catch (e) {
      const statusCode = e?.response?.status;
      const data = e?.response?.data ?? {};

      switch (statusCode) {
        case 429:
          // Pending business guidance, 429 will be 'account_locked' instead of 'throttled'
          setMfaError({ key: 'account_locked', metadata: {} });
          break;
        case 401: {
          if (data.locked) {
            setMfaError({ key: 'account_locked', metadata: {} });
          } else if (data.timeout) {
            setMfaError({ key: 'timeout', metadata: { timeout: data.timeout } });
          } else if (data.remaining) {
            setMfaError({ key: 'bad_value', metadata: { remaining: data.remaining } });
          } else if (data.accountLocked) {
            setCurrentForm(LoginForms.FORCE_PASSWORD_RESET);
          }
          break;
        }
        case 409:
          setCloneToken(e.response?.data?.cloneToken);
          setShowDeviceLimitModal(true);
          break;
        case 403:
          setSnackbarMessage(t('contact_your_company_administrator_for_access'));
          setShowSnackbar(true);
          setSnackbarDuration(180000);
          break;
        default:
          setSnackbarMessage(t('something_went_wrong_please_try_again'));
          setShowSnackbar(true);
          setSnackbarDuration(180000);
          break;
      }
    }
    setIsLoading(false);
  }, [email, password, verificationCode]);

  useEffect(() => {
    if (searchParams.has('token')) {
      const encodedToken = searchParams.get('token');
      const decodedToken = decodeURI(encodedToken);
      (async () => {
        setIsLoading(true);
        try {
          const { user, token } = (await sessionService.checkSession(decodedToken)).data;
          LocalAuth.setToken({ email: user.toLowerCase(), token });
          redirectFinalDestination({ email: user.toLowerCase(), token });
        } catch (e) {
          console.error('Error Retrieving Token', e);
          setSnackbarMessage(t('failed_to_authorize_token'));
          setShowSnackbar(true);
          setSnackbarDuration(180000);
        }
        setIsLoading(false);
      })();
    }
  }, []);

  useEffect(() => {
    if (isInternal(email)) {
      // Redirect back to localhost if running locally
      const localFinal =
        process.env.NODE_ENV === 'local' ? `?finaldestination=${encodeURIComponent('https://localhost:3001')}` : '';

      setIsLoading(true);
      if (searchParams.has('finaldestination')) {
        const destination = validatedFinalDestination(searchParams.get('finaldestination'));
        return redirectToAdfs(`?finaldestination=${destination}`, email);
      } else {
        redirectToAdfs(localFinal, email);
      }
      // Loading spinner is not turned off since the redirect should navigate away from the page.
    }
  }, [email]);

  const isPasswordReset = loginResetForms.includes(currentForm);

  if (isPasswordReset) {
    return (
      <PasswordResetScreens
        currentForm={currentForm}
        setCurrentForm={setCurrentForm}
        handleClearState={handleClearState}
      />
    );
  }

  return (
    <div className={cx(loginStyles.container, cpua4769Web && loginStyles.zoomedHeight)}>
      <div className={loginStyles.loginWindow}>
        {isLoading && (
          <div className={loginStyles.spinnerContainer}>
            <Spinner />
          </div>
        )}
        {closeLogin && (
          <div className={loginStyles.closeIconContainer} onClick={closeLogin}>
            <img className={loginStyles.closeIcon} src={close} alt="close_button" data-testid="exit-login" />
          </div>
        )}
        <div className={loginStyles.loginContainer}>
          <div className={loginStyles.rtsProLogo}>
            <img src={logo} alt="rtspro_blue_logo" />
          </div>
          {currentForm === LoginForms.FORGOT_PASSWORD && (
            <ForgotPassword
              closeForgotPassword={closeForgotPassword}
              setShowSnackbar={setShowSnackbar}
              setSnackbarMessage={setSnackbarMessage}
              setSnackbarDuration={setSnackbarDuration}
              setSnackbarVariant={setSnackbarVariant}
            />
          )}
          {currentForm === LoginForms.VERIFICATION_REQUEST && (
            <RequestVerification navigate={navigate} email={email} setEmail={setEmail} />
          )}
          {currentForm === LoginForms.MFA_LOGIN && (
            <MFALogin
              handleLogin={handleLogin}
              email={email}
              isLoading={isLoading}
              handleEmailChange={handleEmailChange}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              password={password}
              setPassword={setPassword}
              navigateForgotPassword={navigateForgotPassword}
              verificationCode={verificationCode}
              setVerificationCode={setVerificationCode}
              setShowSnackbar={setShowSnackbar}
              setSnackbarMessage={setSnackbarMessage}
              setSnackbarDuration={setSnackbarDuration}
              setSnackbarVariant={setSnackbarVariant}
              mfaError={mfaError}
            />
          )}
          <div>
            <Snackbar
              open={showSnackbar}
              duration={snackbarDuration}
              message={snackbarMessage}
              variant={snackbarVariant || 'error'}
              position="bottomCenter"
              onClose={() => setShowSnackbar(false)}
            />
          </div>
          <DeviceLimitModal
            isOpen={showDeviceLimitModal}
            cloneToken={cloneToken}
            closeModal={closeDeviceLimitModal}
            sendAnalyticsEvent={sendAnalyticsEvent}
          />
        </div>
      </div>
    </div>
  );
};

export default Login;
